import useAuthSessionStore from "../stores/auth-session-store";
import useUserStore from "../stores/user-store";
import { refreshSessionRequest } from "./auth.api";
import { storeCsrfToken } from "./csrf";

let refreshPromise = null;
let interactiveLoginPromise = null;
let interactiveResolve = null;
let interactiveReject = null;

const setBootstrapStepIfPending = (step) => {
    const { sessionBootstrapStatus, setSessionBootstrapStep } = useAuthSessionStore.getState();

    if (sessionBootstrapStatus === "pending") {
        setSessionBootstrapStep(step);
    }
};

export const isAuthFailure = (error) => error?.status === 401;
export const isRefreshAuthFailure = (error) => error?.status === 401 || error?.status === 403;

const getExpiresAtTime = (expiresAt) => {
    if (!expiresAt) {
        return null;
    }

    const time = new Date(expiresAt).getTime();
    return Number.isNaN(time) ? null : time;
};

export const isAccessTokenExpired = (expiresAt) => {
    const expiresAtTime = getExpiresAtTime(expiresAt);

    if (!expiresAtTime) {
        return false;
    }

    return expiresAtTime <= Date.now();
};

export const shouldBootstrapAuthSession = () => {
    const { loggedIn, jwt, accessTokenExpiresAt } = useUserStore.getState();

    if (!loggedIn) {
        return true;
    }

    if (!jwt) {
        return true;
    }

    return isAccessTokenExpired(accessTokenExpiresAt);
};

export const applyAuthSession = (session) => {
    useAuthSessionStore.getState().finishSessionBootstrap();
    storeCsrfToken(session?.csrfToken ?? null);
    useUserStore.getState().setSession(session);
};

export const clearAuthSession = () => {
    useAuthSessionStore.getState().finishSessionBootstrap();
    storeCsrfToken(null);
    useUserStore.getState().logout();
};

export const refreshAccessToken = async () => {
    if (!refreshPromise) {
        setBootstrapStepIfPending("refreshingAccess");

        refreshPromise = refreshSessionRequest()
            .then((session) => {
                setBootstrapStepIfPending("restoringWorkspace");
                applyAuthSession(session);
                return session;
            })
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
};

export const requestInteractiveLogin = () => {
    if (!useUserStore.getState().loggedIn) {
        return Promise.reject({
            status: 401,
            message: "Authentication required.",
            data: null
        });
    }

    if (!interactiveLoginPromise) {
        useAuthSessionStore.getState().openReauthModal();

        interactiveLoginPromise = new Promise((resolve, reject) => {
            interactiveResolve = resolve;
            interactiveReject = reject;
        }).finally(() => {
            interactiveLoginPromise = null;
            interactiveResolve = null;
            interactiveReject = null;
        });
    }

    return interactiveLoginPromise;
};

export const completeInteractiveLogin = (session) => {
    useAuthSessionStore.getState().closeReauthModal();
    interactiveResolve?.(session);
};

export const cancelInteractiveLogin = (error = null) => {
    useAuthSessionStore.getState().closeReauthModal();
    interactiveReject?.(
        error ?? {
            status: 401,
            message: "Authentication required.",
            data: null
        }
    );
};
